import { FP, Rx, RxOperators } from "../deps"

type EventType<T extends string, V> = V & {
  kind: T
}
type Action<T extends string, V = {}> = EventType<`${T}Action`, V>
type Intent<T extends string, V = {}> = EventType<`${T}Intent`, V>
type Internal<T extends string, V = {}> = EventType<`${T}Internal`, V>

export type CartChangeAction = Action<"cartChange">

export type CartImpressionAction = Action<
  "cartImpression",
  { fullPage: boolean }
>

export type CheckoutIntent = Intent<
  "checkout",
  { originSection: "cart" | "sellalongPanel" }
>

export type HelpModalHideIntent = Intent<"helpModalHide">
export type HelpModalShowIntent = Intent<"helpModalShow">

export type LoginCTAIntent = Intent<"loginCta">
export type LoginEmailEntryIntent = Intent<"loginEmailEntry">
export type LoginStartIntent = Intent<"loginStart", { email: string }>

export type PanelImpressionAction = Action<"panelImpression">

export type PurchaseStateTransitionAction = Action<
  "purchaseStateTransition",
  {
    purchaseId: string
    currentState: "OWNED" | "TO_SELL"
  }
>

export type PurchaseStateTransitionIntent = Intent<
  "purchaseStateTransition",
  {
    purchaseId: string
    currentState: "OWNED" | "TO_SELL"
    futureState: "OWNED" | "TO_SELL"
  }
>

export type SellalongBrandCTAIntent = Intent<"sellalongBrandCta">
export type SellalongCTAIntent = Intent<"sellalongCta">
export type SellalongFAQCTAIntent = Intent<"sellalongFaqCta">

export type SellalongComponentRemovedInternal =
  Internal<"sellalongComponentRemoved">

export type Event =
  | CartChangeAction
  | CartImpressionAction
  | CheckoutIntent
  | HelpModalHideIntent
  | HelpModalShowIntent
  | LoginCTAIntent
  | LoginEmailEntryIntent
  | LoginStartIntent
  | PanelImpressionAction
  | PurchaseStateTransitionAction
  | PurchaseStateTransitionIntent
  | SellalongBrandCTAIntent
  | SellalongCTAIntent
  | SellalongFAQCTAIntent
  | SellalongComponentRemovedInternal

export type EventKind = Event["kind"]

export const eventBus = new Rx.Subject<Event>()

export const publish: (event: Event) => void = (event) => eventBus.next(event)

export const createPublishCallback: (event: Event) => () => void =
  (event) => () =>
    publish(event)

export const subscribe: <K extends EventKind>(
  kind: K
) => (cb: (event: EventForKind<K>) => void) => Rx.Subscription =
  (kind) => (cb) =>
    FP.function.pipe(eventBus, RxOperators.filter(isEvent(kind))).subscribe(cb)

export type EventForKind<K extends EventKind> = Extract<Event, { kind: K }>

export const isEvent =
  <K extends EventKind>(kind: K) =>
  (event: Event): event is EventForKind<K> =>
    event.kind === kind
